import { LoadingIndicator } from "components/custom/Loader"
import { KR } from "pages/dashboard/performance/components/objectives/ObjCardComponents"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import { dptKRs } from "../interface/department"
import { useState } from "react"
import { ViewKR } from "pages/dashboard/performance/components/objectives/ViewKR"
import { CreateEditKR } from "pages/dashboard/performance/components/objectives/CreateEditKR"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { Button, P2 } from "components"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { newArchAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores/app-notification"
import KeyResultEvidenceButton from "pages/employee-dashboard/performance-new/reviews/components/KeyResultEvidenceButton"
import { EvidenceRole } from "pages/employee-dashboard/performance-new/reviews/components/KeyResultEvidenceDrawer"
import CreateInitiative from "../individual/CreateInitiative"
import ViewInitiative from "../individual/ViewInitiative"
import { Skeleton, SkeletonLines } from "components/new-stuff/ui/Skeleton"

interface KeyResultRowsListProps {
  data?: dptKRs[] | null
  isLoading?: boolean
  emptyText?: string
  showEmployee?: boolean
  role?: "admin" | "manager" | "employee"
  linkedObjectiveRolePath?: "admin" | "manager" | "employee" | "hod"
  enableActions?: boolean
  allowEdit?: boolean
  allowDelete?: boolean
  allowAddInitiative?: boolean
  allowProgressUpdate?: boolean
  allowManagerScore?: boolean
  viewType?: "edit" | "view"
  employeeId?: number
  assignmentId?: number
  requireEvidence?: boolean
  reviewPeriodId?: number
  deleteEndpointBuilder?: (kr: dptKRs) => string
  onDeleteSuccess?: () => void
  onProgressUpdateSuccess?: () => void
  onManagerScoreSuccess?: () => void
  onManagerScoreSave?: (promise: Promise<boolean>) => void
  onEvidenceSatisfactionChange?: (
    keyResultId: number,
    satisfied: boolean
  ) => void
  onEvidenceSaved?: () => void
}

interface ModalState {
  type: "delete" | "edit" | "view" | "add-initiative" | ""
  data: dptKRs | null
}

const KeyResultRowsList = ({
  data,
  isLoading = false,
  emptyText = "No Key Result.",
  showEmployee = false,
  role = "admin",
  linkedObjectiveRolePath,
  enableActions = false,
  allowEdit = true,
  allowDelete = true,
  allowAddInitiative = false,
  allowProgressUpdate = false,
  allowManagerScore = false,
  viewType = "edit",
  employeeId,
  assignmentId,
  requireEvidence = false,
  reviewPeriodId,
  deleteEndpointBuilder,
  onDeleteSuccess,
  onProgressUpdateSuccess,
  onManagerScoreSuccess,
  onManagerScoreSave,
  onEvidenceSatisfactionChange,
  onEvidenceSaved,
}: KeyResultRowsListProps) => {
  const queryClient = useQueryClient()
  const [modal, setModal] = useState<ModalState>({
    type: "",
    data: null,
  })
  const [expandedKr, setExpandedKr] = useState<ModalState["data"]>(null)
  const [activeProgressUpdate, setActiveProgressUpdate] = useState<
    number | null
  >(null)
  const { toast } = useAppNotificationStore()

  const isEmployeeEdit = role === "employee"

  const {
    isLoading: isUpdatingProgress,
    mutate: updateProgress,
    variables: progressVariables,
  } = useMutation({
    mutationFn: async (body: {
      keyResultId: number
      targetId: number
      currentProgress: number | null
    }) => {
      const res = await newArchAxiosInstance.patch(
        `/api/v2/employee/okr/keyresultprogress`,
        body
      )
      return res.data
    },
    onSuccess: (res) => {
      setActiveProgressUpdate(null)
      onProgressUpdateSuccess?.()
      res?.message && toast.success(res.message)
    },
    onError: onQueryError,
  })

  const {
    isLoading: isUpdatingManagerScore,
    mutateAsync: updateManagerScoreAsync,
    variables: managerScoreVariables,
  } = useMutation({
    mutationFn: async (body: {
      keyResultId: number
      targetId: number
      employeeId?: number
      assigmentId?: number
      managerProgress: number | null
    }) => {
      const res = await newArchAxiosInstance.patch(
        `/api/v2/manager/okr/keyresultprogress`,
        body
      )
      return res.data
    },
    onSuccess: (res) => {
      onManagerScoreSuccess?.()
      res?.message && toast.success(res.message)
    },
    onError: onQueryError,
  })

  const handleManagerScoreSubmit = (
    body: Parameters<typeof updateManagerScoreAsync>[0]
  ) => {
    const promise = updateManagerScoreAsync(body).then(
      () => true,
      () => false
    )
    onManagerScoreSave?.(promise)
  }

  const { isLoading: isDeleting, mutate: deleteKR } = useMutation({
    mutationFn: async (kr: dptKRs) => {
      if (!deleteEndpointBuilder) {
        throw new Error("Delete endpoint builder is not configured")
      }

      const res = await newArchAxiosInstance.delete(deleteEndpointBuilder(kr))
      return res.data
    },
    onSuccess: (res) => {
      setModal({ type: "", data: null })
      onDeleteSuccess?.()
      toast.success(res?.message || "Operation successful")
    },
    onError: onQueryError,
  })

  if (isLoading) {
    return (
      <div className="py-6 flex flex-col gap-3" aria-busy="true">
        <SkeletonLines lines={4} />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full py-10 flex justify-center items-center">
        <h6 className="text-padeBlack">{emptyText}</h6>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        {data.map((kr) => (
          <div key={`kr-row-${kr.employeeKeyResultId}`}>
            <KR.KRItemWrapper loading={false}>
              <KR.KRItemTitle
                title={kr?.keyResultTitle || ""}
                onClick={() =>
                  setExpandedKr(
                    expandedKr?.employeeKeyResultId === kr?.employeeKeyResultId
                      ? null
                      : kr
                  )
                }
              />

              <div className="flex items-center gap-6">
                {showEmployee && (
                  <div className="flex items-center gap-4 justify-start mr-2">
                    <PersonAvatar
                      src={kr?.employeeTargetDto?.[0]?.employeeAvatarUrl}
                      alt=""
                      className="h-5 w-5"
                    />
                    <p className="text-padeBlack font-bold text-xs">
                      {kr?.employeeTargetDto?.[0]?.employeeName || "-"}
                    </p>
                  </div>
                )}

                {/* Evidence button for non-employee sits inline with other row items */}
                {!isEmployeeEdit && !!reviewPeriodId && (requireEvidence || kr.evidenceDto) && (
                  <KeyResultEvidenceButton
                    keyResultId={kr.employeeKeyResultId}
                    reviewPeriodId={reviewPeriodId}
                    keyResultTitle={kr.keyResultTitle || ""}
                    role={role as EvidenceRole}
                    viewType="edit"
                    requireEvidence={requireEvidence}
                    evidenceSummary={kr.evidenceDto}
                    onSatisfactionChange={onEvidenceSatisfactionChange}
                    onEvidenceSaved={onEvidenceSaved}
                  />
                )}

                {allowProgressUpdate &&
                  (kr.metric === 3 ? (
                    <KR.KRSwitch
                      score={kr?.employeeTargetDto?.[0]?.currentProgress || 0}
                      onChange={(checked) => {
                        setActiveProgressUpdate(kr.employeeKeyResultId)
                        updateProgress({
                          keyResultId: kr.employeeKeyResultId,
                          targetId: kr.employeeTargetDto?.[0]?.employeeTargetId,
                          currentProgress: checked ? 100 : 0,
                        })
                      }}
                      disabled={isUpdatingProgress}
                      loading={
                        progressVariables?.keyResultId ===
                          kr.employeeKeyResultId && isUpdatingProgress
                      }
                    />
                  ) : (
                    <KR.KRInput
                      viewType="edit"
                      metric={kr.metric}
                      defaultScore={
                        kr?.employeeTargetDto?.[0]?.currentProgress || 0
                      }
                      targetValue={kr?.employeeTargetDto?.[0]?.endValue ?? 0}
                      onBlur={(value) => {
                        setActiveProgressUpdate(kr.employeeKeyResultId)
                        updateProgress({
                          keyResultId: kr.employeeKeyResultId,
                          targetId: kr.employeeTargetDto?.[0]?.employeeTargetId,
                          currentProgress: value,
                        })
                      }}
                      disabled={isUpdatingProgress}
                    />
                  ))}

                {allowManagerScore && (
                  <div className="flex gap-4">
                    {/* Employee score — read-only for manager */}
                    <div className="flex items-center gap-2">
                      {kr.metric === 3 ? (
                        <KR.KRSwitch
                          score={
                            kr?.employeeTargetDto?.[0]?.currentProgress || 0
                          }
                          disabled
                        />
                      ) : (
                        <KR.KRInput
                          viewType="edit"
                          metric={kr.metric}
                          defaultScore={
                            kr?.employeeTargetDto?.[0]?.currentProgress || 0
                          }
                          targetValue={
                            kr?.employeeTargetDto?.[0]?.endValue ?? 0
                          }
                          onBlur={() => {}}
                          disabled
                        />
                      )}
                    </div>
                    {/* Manager score — editable in edit mode, read-only in view mode */}
                    <div className="flex items-center gap-2">
                      {kr.metric === 3 ? (
                        <KR.KRSwitch
                          score={
                            kr?.employeeTargetDto?.[0]?.managerProgress || 0
                          }
                          onChange={(checked) => {
                            if (viewType === "view") return
                            handleManagerScoreSubmit({
                              keyResultId: kr.employeeKeyResultId,
                              targetId:
                                kr.employeeTargetDto?.[0]?.employeeTargetId,
                              employeeId,
                              assigmentId: assignmentId,
                              managerProgress: checked ? 100 : 0,
                            })
                          }}
                          disabled={isUpdatingManagerScore || viewType === "view"}
                          loading={
                            managerScoreVariables?.keyResultId ===
                              kr.employeeKeyResultId && isUpdatingManagerScore
                          }
                          role="manager"
                        />
                      ) : (
                        <KR.KRInput
                          viewType={viewType}
                          metric={kr.metric}
                          defaultScore={
                            kr?.employeeTargetDto?.[0]?.managerProgress || 0
                          }
                          targetValue={
                            kr?.employeeTargetDto?.[0]?.endValue ?? 0
                          }
                          onBlur={(value) => {
                            if (viewType === "view") return
                            if (
                              value ===
                              (kr?.employeeTargetDto?.[0]?.managerProgress ||
                                0)
                            )
                              return
                            handleManagerScoreSubmit({
                              keyResultId: kr.employeeKeyResultId,
                              targetId:
                                kr.employeeTargetDto?.[0]?.employeeTargetId,
                              employeeId,
                              assigmentId: assignmentId,
                              managerProgress: value,
                            })
                          }}
                          disabled={isUpdatingManagerScore || viewType === "view"}
                        />
                      )}
                    </div>
                  </div>
                )}

                <KR.KRDueDate
                  date={kr?.employeeTargetDto?.[0]?.dueDate || ""}
                />
                <KR.KRWeight
                  weight={kr?.weight}
                  isWeightAssigned={kr?.isWeightAssigned}
                />
                <div>
                  <KR.KRProgress
                    progress={
                      kr?.employeeTargetDto?.[0]?.percentageProgress || 0
                    }
                  />
                  {allowManagerScore && (
                    <KR.KRProgress
                      progress={
                        kr?.employeeTargetDto?.[0]
                          ?.managerPercentageProgress || 0
                      }
                    />
                  )}
                </div>

                {/* Evidence button for employee sits at the far right */}
                {isEmployeeEdit && !!reviewPeriodId && (
                  <div className="ml-auto">
                    <KeyResultEvidenceButton
                      keyResultId={kr.employeeKeyResultId}
                      reviewPeriodId={reviewPeriodId}
                      keyResultTitle={kr.keyResultTitle || ""}
                      role={role as EvidenceRole}
                      viewType="edit"
                      requireEvidence={requireEvidence}
                      evidenceSummary={kr.evidenceDto}
                      onSatisfactionChange={onEvidenceSatisfactionChange}
                      onEvidenceSaved={onEvidenceSaved}
                    />
                  </div>
                )}

                {enableActions && (
                  <KR.KRActionsBtn
                    onView={() => {
                      setModal({ type: "view", data: kr })
                    }}
                    onEdit={
                      allowEdit
                        ? () => {
                            setModal({ type: "edit", data: kr })
                          }
                        : undefined
                    }
                    onAddInitiative={
                      allowAddInitiative
                        ? () => {
                            setModal({ type: "add-initiative", data: kr })
                          }
                        : undefined
                    }
                    onDelete={
                      allowDelete && deleteEndpointBuilder
                        ? () => {
                            setModal({ type: "delete", data: kr })
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            </KR.KRItemWrapper>

            {expandedKr?.employeeKeyResultId === kr?.employeeKeyResultId && (
              <ViewInitiative keyResultId={kr?.employeeKeyResultId} />
            )}
          </div>
        ))}
      </div>

      {modal.type === "view" && (
        <ViewKR
          open={true}
          onClose={() => {
            setModal({ type: "", data: null })
          }}
          data={modal.data as dptKRs}
          role={role}
          linkedObjectiveRolePath={linkedObjectiveRolePath}
        />
      )}

      {modal.type === "edit" && (
        <CreateEditKR
          open={true}
          onClose={() => {
            setModal({ type: "", data: null })
          }}
          role={role}
          krId={modal.data?.employeeKeyResultId}
          deptId={modal.data?.departmentId}
          deptObjId={modal.data?.departmentObjectiveId}
          onSuccess={onDeleteSuccess}
        />
      )}

      {modal.type === "add-initiative" && (
        <CreateInitiative
          keyResultId={modal?.data?.employeeKeyResultId}
          keyResultName={modal?.data?.keyResultTitle || ""}
          handleClose={() => {
            setModal({ type: "", data: null })
          }}
          handleSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: [
                `/api/v2/admin/okr/employee/keyresults/initiatives/${modal?.data?.employeeKeyResultId}`,
              ],
            })
            queryClient.invalidateQueries({
              queryKey: [
                `/api/v2/employee/okr/initiatives/${modal?.data?.employeeKeyResultId}`,
              ],
            })
          }}
          pageType={role}
        />
      )}

      <CustomModal
        open={modal.type === "delete"}
        onCancel={() => {
          setModal({ type: "", data: null })
        }}
        title={`Delete ${modal?.data?.keyResultTitle || "Key Result"}`}
        footer={
          <div className="flex justify-end gap-4">
            <Button
              color="neutral"
              onClick={() => {
                setModal({ type: "", data: null })
              }}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                if (modal.data) {
                  deleteKR(modal.data)
                }
              }}
              disabled={isDeleting}
              submitting={isDeleting}
            >
              Delete
            </Button>
          </div>
        }
      >
        <P2 className="font-normal font-avenir text-neutral500">
          This key result will be permanently removed from the objective. You
          won&apos;t be able to undo this action.
        </P2>
      </CustomModal>
    </>
  )
}

export default KeyResultRowsList
